import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component } from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';

import { AsyncPipe, NgIf } from '@angular/common';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { SubscriptionService } from '../../../settings/shared/modules/subscription/subscription.service';
import { SharedDirectivesModule } from '../../../shared/shared-directives.module';
import { CancellationStatus } from '../../models/customer.models';
import { IntercomService } from '../../services/intercom.service';
import { CustomerState } from '../../store/states/customer/customer.state';
import { GoTo } from '../../store/states/router-nav/router-nav-state.actions';
import { RouterPath } from '../../store/states/router-nav/router-nav-state.model';

@Component({
  selector: 'adspt-subscription-alert',
  templateUrl: 'subscription-alert.component.html',
  styleUrls: ['subscription-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SubscriptionService],
  imports: [
    NgIf,
    NzAlertComponent,
    NzButtonComponent,
    NzWaveDirective,
    AsyncPipe,
    TranslatePipe,
    SharedDirectivesModule,
  ],
})
export class SubscriptionAlertComponent {
  @Select(CustomerState.subscriptionCancellationStatus)
  readonly cancellationStatus$!: Observable<CancellationStatus>;

  @Select(CustomerState.subscriptionCancellationDate)
  readonly cancellationDate$!: Observable<Date | undefined>;

  readonly canceled$: Observable<boolean> = this.store.select(CustomerState.canceled);

  @Dispatch()
  readonly goTo = (path: RouterPath) => new GoTo(path);

  readonly message$: Observable<string> = combineLatest([
    this.cancellationStatus$.pipe(
      map((status) => {
        if (!status) {
          return;
        }

        const isStart = status & CancellationStatus.Start;

        if (status & CancellationStatus.User) {
          return isStart ? 'start-user' : 'restart-user';
        }

        if (status & CancellationStatus.Managed) {
          return isStart ? 'start-managed' : 'restart-managed';
        }

        if (status & CancellationStatus.Undo) {
          return 'undo-cancellation';
        }

        return isStart ? 'start-subscription-admin' : 'restart-subscription-admin';
      }),
    ),
    this.canceled$.pipe(map((canceled) => (canceled ? 'past' : 'future'))),
  ]).pipe(map(([message, prefix]) => (message ? `common.subscription.${prefix}.${message}` : '')));

  readonly action$: Observable<
    { label: string; handler: (status?: CancellationStatus) => void; loading?: Observable<boolean> } | undefined
  > = this.cancellationStatus$.pipe(
    map((status) => {
      if (status & CancellationStatus.User) {
        return;
      }

      if (status & CancellationStatus.Managed) {
        return {
          label: 'common.subscription.contact-support',
          handler: this.onContactSupport,
        };
      }

      if (status & CancellationStatus.Undo) {
        return {
          label: 'common.subscription.undo-cancel',
          handler: this.onUndoCancellation,
          loading: this.ss.restarting$,
        };
      }

      return {
        label:
          status & CancellationStatus.Start
            ? 'common.subscription.start-subscription'
            : 'common.subscription.restart-subscription',
        handler: this.onStartSubscription,
        loading: this.ss.preloading$,
      };
    }),
  );

  readonly onUndoCancellation = () =>
    this.ss
      .restartSubscription()
      .pipe(
        defaultToastError(),
        catchError(() => of(true)),
      )
      .subscribe(() => this.goTo(RouterPath.CustomerSubscriptionSettings));

  readonly onContactSupport = (status: CancellationStatus) =>
    this.is.showNewMessage(
      lastValue(
        this.ts.get(
          status & CancellationStatus.Start
            ? 'common.subscription.intercom-start-subscription'
            : 'common.subscription.intercom-restart-subscription',
        ),
      ),
    );

  readonly onStartSubscription = () =>
    this.ss
      .openSubscriptionDrawer()
      .pipe(filter((response) => response))
      .subscribe(() => this.goTo(RouterPath.CustomerSubscriptionSettings));

  constructor(
    private ss: SubscriptionService,
    private is: IntercomService,
    private ts: TranslateService,
    private store: Store,
  ) {}
}
